<script setup>
import { ref } from "vue";
import FormFileUploader from "../form/FileUploader.vue";
import { IonIcon } from "@ionic/vue";
import { closeCircle } from "ionicons/icons";
import { watch } from "vue";
import { watchDebounced } from "@vueuse/core";

const props = defineProps({
	index: Number,
	personalisationInformation: Object,
	previousLogo: Array,
});

const emits = defineEmits([
	"@removePersonalisationItem",
	"@updatePersonalisationItem",
]);

const usePreviousLogo = ref(false);

const personalisationData = defineModel();
const { locations, types, supporting_text, supporting_links } =
	props.personalisationInformation;

const handleUpdateLocation = (location) => {
	personalisationData.value.location = location.name;
	personalisationData.value.large = location.large;
};

const handleUpdateType = (value) => {
	if (!value) return (personalisationData.price_type = "");
	const type = types.find((type) => type.name === value);
	const lowerCaseType = type.type.toLowerCase().replace(" ", "_");
	personalisationData.value.price_type = lowerCaseType;
};

const handleUsePreviousLogo = (logo) => {
	if (personalisationData.value.logo === logo) {
		personalisationData.value.logo = null;
	} else {
		personalisationData.value.logo = logo;
	}
};

watchDebounced(
	personalisationData.value,
	() => {
		emits("@updatePersonalisationItem");
	},
	{ debounce: 300 }
);

watch(
	() => personalisationData.error,
	() => {
		if (!stockErrorMessage.value) return;
		setTimeout(() => {
			stockErrorMessage.value = null;
		}, 3000);
	}
);
</script>

<template>
	<div>
		<div class="u-flex u-h-right u-mb u-mb-alt@ipd-pro" v-if="index !== 0">
			<span
				class="e-link e-link--grey u-flex u-gap-quarter u-v-center"
				@click="emits('@removePersonalisationItem')"
				>Remove <ion-icon class="u-font-large" :icon="closeCircle"
			/></span>
		</div>

		<template v-if="personalisationData.error">
			<div
				class="c-card c-card--error u-my-half u-px u-py-half u-font-small u-tac"
			>
				{{ personalisationData.error }}
			</div>
		</template>

		<div
			v-if="
				!personalisationData.logo ||
				!previousLogo.includes(personalisationData.logo)
			"
		>
			<FormFileUploader v-model="personalisationData.logo" />
		</div>

		<div v-else class="c-card--upload u-p u-p-alt@ipad-pro u-height-auto">
			<img
				:src="personalisationData.logo"
				alt="Previous Logo"
				class="u-max-100px"
			/>
		</div>

		<div v-if="previousLogo" class="u-mt">
			<p class="u-font-bold">Re-use existing logo</p>

			<div class="o-layout o-layout--gutter-small u-mt-half">
				<div
					class="o-layout__item u-width-1/4 u-pos-rel"
					v-for="(logo, index) in previousLogo"
					:key="index"
				>
					<div class="e-media e-media--1-1 e-media--overlay">
						<img
							v-if="
								logo &&
								!logo.includes('.pdf') &&
								!logo.includes('.ai') &&
								!logo.includes('.eps')
							"
							:src="logo"
							alt="Previous Logo"
							class="e-media e-media--cover"
						/>
						<div v-else>
							<p
								class="u-cover u-flex u-h-center u-v-top u-p-half u-tac u-font-small"
							>
								Unsupported file type
							</p>
						</div>
						<input
							type="checkbox"
							id="usePreviousLogo"
							class="u-checkbox"
							:checked="logo === personalisationData.logo"
							@change="handleUsePreviousLogo(logo)"
						/>
					</div>
				</div>
			</div>
		</div>

		<div class="u-flex u-wrap u-no-wrap@ipad u-gap-half u-mt u-mt-alt@ipad-pro">
			<template v-for="(location, index) in locations" :key="index">
				<div @click="handleUpdateLocation(location)" class="u-pointer">
					<div
						class="c-card c-card--location u-p-half"
						:class="{
							'c-card--active': personalisationData.location === location.name,
						}"
					>
						<img :src="location.icon" alt="" />
					</div>
					<p class="u-tac u-mt-half u-font-small u-font-colour-secondary">
						{{ location.name }}
					</p>
				</div>
			</template>
		</div>

		<div
			class="u-flex u-wrap u-no-wrap@ipad u-gap u-gap-alt@ipad-pro u-mt u-mt-alt@ipad-pro"
		>
			<div class="u-flex u-gap-half u-v-center u-width-1/1 u-width-1/2@ipad">
				<span class="u-no-whitespace">Type</span>
				<select
					value=""
					name="type"
					class="e-select u-flex-even"
					v-model="personalisationData.type"
					@change="handleUpdateType($event.target.value)"
				>
					<option value="">Select type</option>
					<template v-for="(type, index) in types" :key="index">
						<option :value="type.name">{{ type.name }}</option>
					</template>
				</select>
			</div>

			<div class="u-flex u-gap-half u-v-center u-width-1/1 u-width-1/2@ipad">
				<span class="u-no-whitespace">Colours</span>
				<select
					name="type"
					class="e-select"
					v-model="personalisationData.numberOfColours"
				>
					<option value="">Select colours in logo</option>
					<template v-for="i in 12" :key="i">
						<option :value="i">{{ i }}</option>
					</template>
				</select>
			</div>
		</div>

		<div class="u-mt u-mt-alt@ipad-pro" v-if="index === 0">
			<p v-if="supporting_text" class="u-font-large">{{ supporting_text }}</p>

			<!-- TODO add these as poopups as per the old site -->
			<!-- <div class="u-flex u-gap u-mt u-mt-alt@ipad-pro" v-if="supporting_links">
				<template v-for="(link, index) in supporting_links" :key="index">
					<a
						:href="link.link.url"
						class="e-link e-link--grey u-flex@ipad u-gap-quarter u-v-center"
					>
						<img
							class="e-media e-media--icon u-mb-quarter u-mb-0@ipad"
							:src="link.icon"
							:alt="link.link.title"
						/>
						<span>{{ link.link.title }}</span>
					</a>
				</template>
			</div> -->
		</div>
	</div>
</template>
